import styles from "../assets/styles/field.module.css"
import { useRef, useEffect, useState, useCallback } from "react";
//important note: red is on the left for the default field image

// Module-level cache so the field image only ever has to be fetched/decoded once per page
// load, in the background, instead of every time a Field instance mounts (e.g. switching
// between the Auto/Tele/Post pages unmounts and remounts this component). Without this, a
// slow/flaky venue-wifi network request for the image could leave the popup blank until a
// full page reload forced a fresh load.
const fieldImageCache = new Map(); // imageSrc -> HTMLImageElement

// Kicks off the background fetch/decode as early as possible (e.g. on app start), well
// before the Field component itself ever mounts or the user taps into the popup.
export function preloadFieldImage(imageSrc) {
    loadFieldImage(imageSrc, () => {});
}

function loadFieldImage(imageSrc, onReady) {
    let img = fieldImageCache.get(imageSrc);
    if (!img) {
        img = new Image();
        img.src = imageSrc;
        fieldImageCache.set(imageSrc, img);
    }
    if (img.complete && img.naturalWidth > 0) {
        onReady(img);
    } else {
        img.addEventListener("load", () => onReady(img), { once: true });
    }
}

function Field({ currentlyActive, imageSrc, initialData, onChange, onRequestClose, rows = 100, cols = 100, imageWid, imageHght, allianceColor, gamemode  }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const controlsRef = useRef(null);
    const cellSizeRef = useRef(12);
    const pointsRef = useRef([]); 
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const overlayRef = useRef(null)
    const imageRef = useRef(null);
    const [flipped, setFlipped] = useState(() => {
        try {
            const stored = localStorage.getItem("field_flipped");
            return stored === "true"; // default false if null
        } catch {
            return false;
        }
    });

    let maxCol = 100;
    let maxRow = 53

    const [drawing, setDrawing] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [colorPicker, setColorPicker] = useState(localStorage.getItem("coloring") == null ? "#00ff00" : localStorage.getItem("coloring"));
    const [shootingPosDisabled, setShootingPosDisabled] = useState(true);
    const [eraserEnabled, setEraserEnabled] = useState(false)

    const wasActiveRef = useRef(false);
    const lastCellRef = useRef(null);
    const repeatStartTimeoutRef = useRef(null);
    const repeatIntervalRef = useRef(null);
    const skipNextActionClickRef = useRef(false);
    const onChangeRef = useRef(onChange);
    const currentlyActiveRef = useRef(currentlyActive);


    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        currentlyActiveRef.current = currentlyActive;
    }, [currentlyActive]);

    const updateHistoryFlags = () => {
        setCanUndo(undoStackRef.current.length > 0);
        setCanRedo(redoStackRef.current.length > 0);
    };

    const stopRepeatAction = useCallback(() => {
        if (repeatStartTimeoutRef.current) {
            clearTimeout(repeatStartTimeoutRef.current);
            repeatStartTimeoutRef.current = null;
        }
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current);
            repeatIntervalRef.current = null;
        }
    }, []);

    const startRepeatAction = useCallback((action) => {
        stopRepeatAction();
        skipNextActionClickRef.current = false;

        repeatStartTimeoutRef.current = setTimeout(() => {
            skipNextActionClickRef.current = true;
            action();
            repeatIntervalRef.current = setInterval(action, 90);
        }, 260);
    }, [stopRepeatAction]);

    const handleActionButtonClick = (action) => {
        if (skipNextActionClickRef.current) {
            skipNextActionClickRef.current = false;
            return;
        }
        action();
    };
    function invert(hex) {
        const r = 255 - parseInt(hex.slice(1, 3), 16);
        const g = 255 - parseInt(hex.slice(3, 5), 16);
        const b = 255 - parseInt(hex.slice(5, 7), 16);
        return `rgb(${r}, ${g}, ${b})`;
}
    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imageRef.current;

        const shouldShootPosDisabled =
            pointsRef.current.length === 0 || 
            pointsRef.current[pointsRef.current.length - 1] === pointsRef.current[pointsRef.current.length - 2];
        setShootingPosDisabled(shouldShootPosDisabled);
        
        if (!canvas || !img) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        ctx.save();

        if (flipped) {
            ctx.translate(canvas.width, canvas.height);
            ctx.rotate(Math.PI);
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        //this section for graying out the wrong side of field
        if(allianceColor !== "n" && gamemode !== 0){
            ctx.save();
            ctx.globalAlpha = 0.45;
            ctx.fillStyle = "black";
            const thirdWidth = canvas.width / 3
            let grayLeftSide;
            if (allianceColor === "b") {
                grayLeftSide = true;
            } else if (allianceColor === "r") {
                grayLeftSide = false;
            }
    
            if (grayLeftSide) {
                ctx.fillRect(0, 0, thirdWidth, canvas.height);
            } else {
                ctx.fillRect(2*thirdWidth, 0, thirdWidth, canvas.height);
            }
            ctx.restore();
        }

        //this section for drawing the points
        const cellSize = cellSizeRef.current;
        ctx.fillStyle = "#00ff00";
        pointsRef.current.forEach((p,index) => {
            const [x, y] = p.split(",").map(Number);
            if (Number.isNaN(x) || Number.isNaN(y)) return;
            if(index > 0 && pointsRef.current[index - 1] == p){
                ctx.fillStyle = invert(colorPicker)
            }
            else{
                ctx.fillStyle = colorPicker
            }
            ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
        });

        ctx.restore();
    }, [flipped, allianceColor,colorPicker]);

    function currentPosButton(){
        const point = pointsRef.current[pointsRef.current.length-1]
        if (!point) return;
        pointsRef.current.push(point);

        undoStackRef.current.push({
            type: "draw",
            point
        });
        redoStackRef.current = [];
        updateHistoryFlags();
        redrawCanvas();
    } 

    
    /**
     * Redraws canvas when fieldmap is flipped
     */
    useEffect(() => {
        if (!currentlyActive) return;
        redrawCanvas();
    }, [flipped, allianceColor, redrawCanvas, currentlyActive]);

    /**
     * Updates localstorage with the current flip value
     */
    useEffect(() => {
        try {
            localStorage.setItem("field_flipped", flipped.toString());
        } catch {}
    }, [flipped]);

    /**
     * Checks if the spot the user wishes to draw is allowed based on the alliance color
     */
    const isBlocked = (x) => {
        if (allianceColor === "n" || gamemode === 0) return false;
        const thirdPoint = cols / 3;
        let blocked;

        // (unflipped): red = left, blue = right
        if(flipped){
            if (allianceColor === "r") { blocked = x < thirdPoint;
            } else if (allianceColor === "b") { blocked = x >= 2*thirdPoint; 
            } else { return false; }
        } else {
            if (allianceColor === "r") { blocked = x >= 2*thirdPoint;
            } else if (allianceColor === "b") { blocked = x < thirdPoint; 
            } else { return false; }
        }
        return blocked;
    };

    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const rect = container.getBoundingClientRect();
        if (rect.width <= 0) return;

        const controlsHeight = controlsRef.current
            ? controlsRef.current.getBoundingClientRect().height
            : 0;
        const availableHeight = Math.max(180, window.innerHeight - controlsHeight - 18);
        const widthFromHeight = availableHeight * (imageWid / imageHght);
        const width = Math.min(rect.width, widthFromHeight);
        const height = width * (imageHght / imageWid);

        canvas.width = width;
        canvas.height = height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        cellSizeRef.current = width / cols;
        redrawCanvas();
    }, [cols, redrawCanvas]);

    /**
     * when popup goes from open → closed: 
     * update the datamap in scout.js with the new gridmap
     */
    useEffect(() => {
        const incomingPoints = Array.isArray(initialData) ? initialData : [];
        const incomingSignature = JSON.stringify(incomingPoints);
        const currentSignature = JSON.stringify(pointsRef.current);

        if (currentlyActive && !wasActiveRef.current) {
            // Keep history when reopening the same path, but reset if external data changed.
            if (incomingSignature !== currentSignature) {
                pointsRef.current = [...incomingPoints];
                
                undoStackRef.current = incomingPoints.map(point => ({
                    type: "draw",
                    point
                }));

                redoStackRef.current = [];
                lastCellRef.current = null;
            }
            updateHistoryFlags();

            resizeCanvas();
            redrawCanvas();
        }

        if (wasActiveRef.current && !currentlyActive && onChangeRef.current) {
            onChangeRef.current([...pointsRef.current]);
        }
        if (!currentlyActive) {
            stopRepeatAction();
        }

        wasActiveRef.current = currentlyActive;
    }, [currentlyActive, initialData, resizeCanvas, redrawCanvas, stopRepeatAction]);

    /**
     * Loads the field image in the background (via the module-level cache above), so it's
     * already decoded and ready well before the user taps to open the popup.
     */
    useEffect(() => {
        loadFieldImage(imageSrc, (img) => {
            imageRef.current = img;
            redrawCanvas();
        });
    }, [imageSrc, redrawCanvas]);

    /**
     * Allows for resizing of the field map
     */
    useEffect(() => {
        if (!currentlyActive) return;

        const handleResize = () => {
            resizeCanvas();
        };

        window.addEventListener("resize", handleResize);
        resizeCanvas();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [currentlyActive, resizeCanvas]);

    /**
     * Undoes the last drawn point on the field map
     */
    const undo = useCallback(() => {
        if (undoStackRef.current.length === 0) return;

        const action = undoStackRef.current.pop();
        if (!action) return;

        if (action.type === "draw") {
            pointsRef.current.pop();
        } else if (action.type === "clear") {
            pointsRef.current = [...action.points];
        } else if(action.type === "erase"){
            pointsRef.current.splice(action.index,0,action.point)
        }

        redoStackRef.current.push(action);
        updateHistoryFlags();
        redrawCanvas();
    }, [redrawCanvas]);

    /**
     * Redraws the last undone point on the field map
     */
    const redo = useCallback(() => {
        if (redoStackRef.current.length === 0) return;

        const action = redoStackRef.current.pop();
        if (!action) return;

        if (action.type === "draw") {
            pointsRef.current.push(action.point);
        } else if (action.type === "clear") {
            pointsRef.current = [];
        }

        undoStackRef.current.push(action);
        updateHistoryFlags();
        redrawCanvas();
    }, [redrawCanvas]);

    /**
     * Removes all points on the field map
     * @returns 
     */
    const clearAllPoints = () => {
        if (pointsRef.current.length === 0) return;

        undoStackRef.current.push({
            type: "clear",
            points: [...pointsRef.current],
        });
        redoStackRef.current = [];
        pointsRef.current = [];
        lastCellRef.current = null;

        updateHistoryFlags();
        redrawCanvas();
    };

    /**
     * Handles specific keypresses for the field map
     */
    useEffect(() => {

        if (!currentlyActive) return;

        const handleKeyDown = (e) => {
            const accuracyPosKey = e.key.toLowerCase();
            if (accuracyPosKey === "s") {
                const shouldShootPosDisabled =
                    pointsRef.current.length === 0 || 
                    pointsRef.current[pointsRef.current.length - 1] === pointsRef.current[pointsRef.current.length - 2];
                if (shouldShootPosDisabled){
                    return;
                }
                e.preventDefault();
                currentPosButton();
                return;
            }
            const shortcutHeld = e.ctrlKey || e.metaKey;
            if (!shortcutHeld) return;

            const key = e.key.toLowerCase();
            if (key === "z" && !e.shiftKey) {
                e.preventDefault();
                undo();
                return;
            }

            if (key === "y" || (key === "z" && e.shiftKey)) {
                e.preventDefault();
                redo();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [currentlyActive, undo, redo]);

    useEffect(() => {
        return () => {
            stopRepeatAction();

            // If the popup unmounts while still active (page switch), preserve the latest edits.
            if (currentlyActiveRef.current && onChangeRef.current) {
                onChangeRef.current([...pointsRef.current]);
            }
        };
    }, [stopRepeatAction]);

    useEffect(() => {
        const handleKeyDown = (e) => {
        if (currentlyActive && (e.key === 'Escape')) {
            e.preventDefault();
            e.stopPropagation();
            onRequestClose();
        }
        };

        const handleClickOutside = (e) => {
        if (currentlyActive && overlayRef.current === e.target) {
            onRequestClose();
        }
        };
        
        if (currentlyActive) {
            window.addEventListener('keydown', handleKeyDown, true); 
            document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('mousedown', handleClickOutside);
        }}

    }, [onRequestClose, currentlyActive]);

    const getCell = (clientX, clientY) => {
        const cellSize = cellSizeRef.current;
        const rect = canvasRef.current.getBoundingClientRect();

        let x = Math.floor((clientX - rect.left) / cellSize);
        let y = Math.floor((clientY - rect.top) / cellSize);  
        if (flipped) {
            x = maxCol - 1 - x;
            y = maxRow - 1 - y;
        }
        return {x, y };
    };

    const drawCell = (x, y) => {
        if (x < 0 || y < 0 || x >= cols || y >= rows) return;

        let canonicalX = x;
        let canonicalY = y;

        if (flipped) {
            canonicalX = maxCol - 1 - x;
            canonicalY = maxRow - 1 - y;
        }

        if (isBlocked(canonicalX)) return;

        const key = `${x},${y}`;
        if (lastCellRef.current === key) return;

        lastCellRef.current = key;
        pointsRef.current.push(key);
        undoStackRef.current.push({ type: "draw", point: key });
        redoStackRef.current = [];

        updateHistoryFlags();
        redrawCanvas();
    };

    const eraseCell = (x,y) => {    
        if (x < 0 || y < 0 || x >= cols || y >= rows) return;

        let canonicalX = x;
        let canonicalY = y;

        if (flipped) {
            canonicalX = maxCol - 1 - x;
            canonicalY = maxRow - 1 - y;
        }

        if (isBlocked(canonicalX)) return;

        const key = `${x},${y}`;
        if (lastCellRef.current === key) return;

        lastCellRef.current = key;
        for(let i = pointsRef.current.length-1; i>=0; i--){
            if(key == pointsRef.current[i]){
                pointsRef.current.splice(i,1)
                undoStackRef.current.push({ type: "erase", point: key, index: i });
                break;
            }
        }
        redoStackRef.current = [];
        updateHistoryFlags();
        redrawCanvas();
    };

    const handlePointerDown = (e) => {
        if (!e.isPrimary) return;

        e.preventDefault();
        lastCellRef.current = null;
        canvasRef.current.setPointerCapture(e.pointerId);
        setDrawing(true);

        const { x, y } = getCell(e.clientX, e.clientY);
        if(!eraserEnabled){
            drawCell(x, y);
        }
        else{
            eraseCell(x,y)
        }
    };

    const handlePointerMove = (e) => {
        if (!drawing || !e.isPrimary) return;

        e.preventDefault();
        const { x, y } = getCell(e.clientX, e.clientY);
        if(!eraserEnabled){
            drawCell(x, y);
        }
        else{
            eraseCell(x,y)
        }    };
    
    const handlePointerUp = (e) => {
        setDrawing(false);
        lastCellRef.current = null;
        try {
        canvasRef.current.releasePointerCapture(e.pointerId);
        } catch {} 
    };
    if(currentlyActive){
            return (
            <div
                className={styles.popup}
                ref={overlayRef}
            >
                <div className={styles.panel}>
                    <button
                        className={styles.exitButton}
                        onClick={onRequestClose}
                    >
                        ✕
                    </button>
                    <div ref={controlsRef} className={styles.controls}>
                        <div className={styles.toolbarButtons}>
                            <button
                                className={styles.toolbarButton}
                                onPointerDown={() => startRepeatAction(undo)}
                                onPointerUp={stopRepeatAction}
                                onPointerLeave={stopRepeatAction}
                                onPointerCancel={stopRepeatAction}
                                onClick={() => handleActionButtonClick(undo)}
                                disabled={!canUndo}
                            >
                                ↺
                            </button>
                            <button
                                className={styles.toolbarButton}
                                onPointerDown={() => startRepeatAction(redo)}
                                onPointerUp={stopRepeatAction}
                                onPointerLeave={stopRepeatAction}
                                onPointerCancel={stopRepeatAction}
                                onClick={() => handleActionButtonClick(redo)}
                                disabled={!canRedo}
                            >
                                ↻
                            </button>
                            <button
                                className={`${styles.toolbarButton} ${
                                    !eraserEnabled ? styles.eraserButtonActive : ''
                                }`}                                
                                onClick = {() => setEraserEnabled(!eraserEnabled)}
                            >
                                Erase
                            </button>

                            <button
                                className={styles.toolbarButton}
                                onClick={() => setFlipped(f => !f)}
                                >
                                ⇄
                            </button>
                            <button id = "shooting" className = {`${styles.toolbarButton}`} disabled = {shootingPosDisabled}
                                onClick = {() => {
                                    currentPosButton()

                                
                            }}>⊙</button>
                            <button className={`${styles.toolbarButton} ${styles.clearButton}`} onClick={clearAllPoints} disabled={pointsRef.current.length === 0}>Clear</button>
                            <input className = {styles.colorPick} value={colorPicker} onChange={(e) => {
                                setColorPicker(e.target.value)
                                localStorage.setItem("coloring", e.target.value)
                            } } type = "color"></input>
                            <button className = {styles.toolbarButton} onClick = {() => {
                                localStorage.removeItem("coloring");
                                setColorPicker("#00ff00");}
                            } >Reset Color</button>

                        </div>
                        <span className={styles.toolbarHint}>Ctrl+Z undo, Ctrl+Y/Ctrl+Shift+Z redo, S shooting position</span>
                    </div>
                    <div ref={containerRef} className={styles.container}>
                        <canvas
                        ref={canvasRef}
                        className={styles.canvas}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                        />
                    </div>
                </div>
            </div>
        );
    }
    else{
        return <p className="invisible">hello</p>
    }
    
}
export default Field;